import PageMeta from '@/components/PageMeta';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const PARAGRAPHS = [
  'I never expected to build a technology company.',
  'For most of my life, I was a singer. Movement, expression, energy, and connection were a large part of who I was. My body was something I relied on without having to question every small action.',
  'Then everything changed.',
  'After being diagnosed with blood cancer twice and going through intensive treatment, I experienced what it feels like to lose trust in your own body.',
  'Pain, fatigue, medication, long hospital stays, physical weakness, and uncertainty changed my relationship with movement. Damage to my spine meant that even an ordinary exercise could feel serious. Like many people, I received physiotherapy guidance and exercises to follow away from the clinic.',
  'But once I was home, the confidence was not always there.',
  'I found myself asking questions that many people quietly ask:',
];

const QUESTIONS = [
  'Am I doing this correctly?',
  'Does this movement suit my circumstances?',
  'Could I be placing stress somewhere without realising it?',
  'Why do I feel unsure even when I have already been shown what to do?',
];

const AFTER = [
  'Then I realised this was not just my problem.',
  'During treatment and recovery, I spoke with other people, physiotherapists, nurses, support workers, and families. Different lives and different circumstances kept revealing the same gap.',
  'People wanted to move.',
  'They wanted to rebuild strength, mobility, independence, and confidence.',
  'But some were exhausted or heavily medicated. Some forgot what they had been shown. Some found printed instructions difficult to understand. Some believed they were performing a movement correctly when their body was doing something different. Others were frightened of getting it wrong.',
  'Professional guidance existed, but it could not be physically present in every home, every day.',
  'That raised a question I could not ignore:',
];

const Q2 = [
  'Could a phone help bridge part of that gap?',
  'Could technology help people better understand movement between appointments?',
  'Could it offer clearer feedback and reassurance without pretending to replace the professionals responsible for their care?',
];

const CLOSING = [
  'That question became the beginning of Valanti Labs.',
  'Valanti Labs is developing AI-powered movement analysis using smartphone video. The aim is to make meaningful movement feedback more accessible and help people rebuild confidence in how they move.',
  'The mission began through adversity, but it is much bigger than any one illness or diagnosis.',
  'At different points in life, people may be recovering from illness, injury, surgery, pain, disability, deconditioning, fatigue, ageing, or a long period away from normal movement.',
  'Others simply want a clearer understanding of their body and movement quality.',
  'My experience opened the door.',
  'The mission is to build something that can serve people far beyond my own story.',
  'Because losing confidence in your body can be isolating — and rebuilding it should feel more supported.',
];

export default function WhyValantiLabs() {
  return (
    <article>
      <PageMeta
        title="Why Valanti Labs exists"
        description="The founder story of Valanti Labs — written by Chrissa Valanti Kathopoulis."
      />
      <div className="vl-container py-16 lg:py-24 max-w-3xl">
        <p className="mono-label text-vl-violet">The founder story</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-5 text-vl-ink">
          Why Valanti Labs exists.
        </h1>

        <div className="mt-12 space-y-6 text-[18px] text-vl-ink/80 leading-[1.75]">
          {PARAGRAPHS.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
          <ul className="pl-6 space-y-2 text-vl-ink/85 italic">
            {QUESTIONS.map((q) => (
              <li key={q}>— {q}</li>
            ))}
          </ul>
          {AFTER.map((p, i) => (
            <p key={`a${i}`}>{p}</p>
          ))}
          <ul className="pl-6 space-y-2 text-vl-ink/85 italic">
            {Q2.map((q) => (
              <li key={q}>— {q}</li>
            ))}
          </ul>
          {CLOSING.map((p, i) => (
            <p key={`c${i}`}>{p}</p>
          ))}
        </div>

        <blockquote className="mt-16 border-l-2 border-vl-violet pl-8 py-2 font-serif-display text-2xl sm:text-3xl text-vl-ink leading-snug">
          "My experience opened the door. The mission is to build something that can serve people far beyond my own
          story."
        </blockquote>

        <div className="mt-10 pt-8 border-t hairline">
          <p className="font-serif-display text-xl text-vl-ink">— Chrissa Valanti Kathopoulis</p>
          <p className="text-sm text-vl-ink/60 mt-1">Founder &amp; Director, Valanti Labs Pty Ltd</p>
        </div>

        <div className="mt-16">
          <Link
            to="/early-access"
            className="inline-flex items-center gap-2 rounded-full bg-vl-ink text-vl-bg px-6 py-3.5 text-sm font-medium hover:bg-vl-violet transition-colors"
          >
            Join the early access list <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </article>
  );
}
